"use client";

import React from "react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SafeEmailBody } from "@/components/crm/messages/components/SafeEmailBody";
import type { TimelineItem } from "@/lib/hooks/api/timeline/enhanced-timeline";
import { File, User, Clock, ExternalLink, Inbox, Send, CheckSquare, Link, X } from "lucide-react";
import { getChannelVisual } from "../utils/channel-visuals";

interface GenericTimelineItemDetailProps {
  item: TimelineItem;
  onClose: () => void;
  onCopyLink?: (item: TimelineItem) => void;
}

export const GenericTimelineItemDetail: React.FC<GenericTimelineItemDetailProps> = ({
  item,
  onClose,
  onCopyLink,
}) => {
  const { Icon, chipClass, iconClass, label: channelLabel } = getChannelVisual(item);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "PPpp");
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-full ${chipClass}`}>
            <Icon className={`h-5 w-5 ${iconClass}`} />
          </div>
          <div>
            <h2 className="text-lg font-semibold line-clamp-1">{item.title}</h2>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Badge variant="secondary" className={`${chipClass} ${iconClass}`}>
                {channelLabel}
              </Badge>
              {item.direction && (
                <div className="flex items-center gap-1">
                  {item.direction === "inbound" ? (
                    <Inbox className="h-3 w-3" />
                  ) : (
                    <Send className="h-3 w-3" />
                  )}
                  <span className="capitalize">{item.direction}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {onCopyLink && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCopyLink(item)}
              title="Copy link to this item"
            >
              <Link className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose} title="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium">Date:</div>
                <div className="text-sm">{formatDate(item.occurred_at)}</div>
              </div>
            </div>

            {item.sender && (
              <div className="flex items-start space-x-3">
                <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium">From:</div>
                  <div className="text-sm">
                    {item.sender.name} {item.sender.email && `<${item.sender.email}>`}
                  </div>
                </div>
              </div>
            )}

            {item.recipients && item.recipients.length > 0 && (
              <div className="flex items-start space-x-3">
                <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium">To:</div>
                  <div className="text-sm">
                    {item.recipients.map((r, index) => (
                      <span key={index}>
                        {r.name} {r.email && `<${r.email}>`}
                        {index < item.recipients!.length - 1 && ", "}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {item.status && (
              <div className="flex items-start space-x-3">
                <CheckSquare className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Status:</div>
                  <Badge variant="outline" className="text-xs capitalize">
                    {item.status}
                  </Badge>
                </div>
              </div>
            )}

            {item.provider && (
              <div className="flex items-start space-x-3">
                <ExternalLink className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Provider:</div>
                  <Badge variant="secondary" className="text-xs capitalize">
                    {item.provider.replace("-", " ")}
                  </Badge>
                </div>
              </div>
            )}
          </div>

          <Separator />

          {item.body?.trim() ? (
            <div className="space-y-2">
              <div className="text-sm font-medium">Content:</div>
              <div className="rounded-lg border bg-muted/30 p-4">
                <SafeEmailBody body={item.body} variant="full" collapseQuotedReplies />
              </div>
            </div>
          ) : null}

          {item.metadata && Object.keys(item.metadata).length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium">Additional Details:</div>
              <div className="bg-muted/20 rounded-lg border p-3">
                <div className="grid grid-cols-1 gap-2 text-xs">
                  {Object.entries(item.metadata).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-muted-foreground capitalize">
                        {key.replace(/([A-Z])/g, " $1").toLowerCase()}:
                      </span>
                      <span className="font-mono">
                        {typeof value === "object" ? JSON.stringify(value) : String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {item.attachments && item.attachments.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium">Attachments:</div>
              <div className="space-y-1">
                {item.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 p-2 bg-muted/20 rounded border"
                  >
                    <File className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{attachment.name || `Attachment ${index + 1}`}</span>
                    {attachment.size && (
                      <span className="text-xs text-muted-foreground">
                        ({Math.round(attachment.size / 1024)}KB)
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
